/** The TanStack values, in one module like `./tanstackTypes`, and the one feature set every table here registers. */
// Lint lets only this file, `./tanstackTypes`, `type.tsx` and `createColumnHelper.tsx` import TanStack.
import type { RankingInfo } from '@tanstack/match-sorter-utils';
import {
    columnFacetingFeature,
    columnFilteringFeature,
    columnOrderingFeature,
    columnPinningFeature,
    columnResizingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createExpandedRowModel,
    createFacetedMinMaxValues,
    createFacetedRowModel,
    createFacetedUniqueValues,
    createFilteredRowModel,
    createSortedRowModel,
    filterFns,
    globalFilteringFeature,
    metaHelper,
    rowExpandingFeature,
    rowSelectionFeature,
    rowSortingFeature,
    sortFns,
    tableFeatures,
    type TableOptions,
    type TableState,
    useTable,
} from '@tanstack/react-table';

import type { DataStackFeatures, RowData, Table } from './tanstackTypes';
import { buildFilter, dateSortFn, monetaryValueSortFn, quantitySortFn } from './utils/utils';

export { flexRender, stateSlicesEqual } from '@tanstack/react-table';

export const dataStackFeatures = tableFeatures({
    columnFilteringFeature,
    globalFilteringFeature,
    columnFacetingFeature,
    rowSortingFeature,
    rowExpandingFeature,
    rowSelectionFeature,
    columnOrderingFeature,
    columnPinningFeature,
    columnVisibilityFeature,
    columnSizingFeature,
    columnResizingFeature,
    filteredRowModel: createFilteredRowModel(),
    sortedRowModel: createSortedRowModel(),
    expandedRowModel: createExpandedRowModel(),
    facetedRowModel: createFacetedRowModel(),
    facetedMinMaxValues: createFacetedMinMaxValues(),
    facetedUniqueValues: createFacetedUniqueValues(),
    // `'auto'` only resolves registered names, and hand-written columns default to it.
    filterFns: { ...filterFns, buildFilter },
    sortFns: { ...sortFns, monetaryValue: monetaryValueSortFn, date: dateSortFn, quantity: quantitySortFn },
    filterMeta: metaHelper<{ itemRank: RankingInfo }>(),
});

/**
 * `useTable` with `dataStackFeatures`. The object it returns is new on every render; its methods and `store` are the
 * same for the component's lifetime. Its `options` are this render's raw options, without v9's feature defaults: read
 * only `meta` from them.
 */
export function useCoreTable<TData extends RowData>(
    options: Omit<TableOptions<DataStackFeatures, TData>, 'features'>,
    selector?: (state: TableState<DataStackFeatures>) => unknown,
): Table<TData> {
    // Without a selector the component re-renders on every state change, internal `columnResizing` included.
    // v9 collapses expanded rows whenever `data` changes; v8 never did.
    return useTable({ autoResetExpanded: false, ...options, features: dataStackFeatures }, selector);
}
